import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import {
  createProductGroup,
  createProductGroupItem,
  deleteProductGroup,
  deleteProductGroupItem,
  getProductGroupImages,
  getProductGroups,
  setProductGroupImage,
  updateProductGroup,
} from "data/server_apis/commodityService";

const productGroupAdapter = createEntityAdapter({
  selectId: (productGroup) => productGroup.id,
});

export const getProductGroupsThunk = createAsyncThunk(
  "productGroups/get",
  async (id, { rejectWithValue }) => {
    try {
      return (await getProductGroups(id)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const getProductGroupImagesThunk = createAsyncThunk(
  "productGroups/get-images",
  async (id, { rejectWithValue }) => {
    try {
      return (await getProductGroupImages(id)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const setProductGroupImageThunk = createAsyncThunk(
  "productGroup/set-image",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return (await setProductGroupImage(id, data)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const createProductGroupThunk = createAsyncThunk(
  "productGroups/create",
  async (data, { rejectWithValue }) => {
    try {
      return (await createProductGroup(data)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const updateProductGroupThunk = createAsyncThunk(
  "productGroup/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return (await updateProductGroup(id, data)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const deleteProductGroupThunk = createAsyncThunk(
  "productGroup/delete",
  async (id, { rejectWithValue }) => {
    try {
      return (await deleteProductGroup(id)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const createProductGroupItemThunk = createAsyncThunk(
  "productGroupItems/create",
  async (data, { rejectWithValue }) => {
    try {
      return (
        await createProductGroupItem({
          ...data,
          product_group: data.product_group.id,
        })
      ).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const deleteProductGroupItemThunk = createAsyncThunk(
  "productGroupItem/delete",
  async ({ id, group }, { rejectWithValue }) => {
    try {
      return (await deleteProductGroupItem(id)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);
const productGroupsSlice = createSlice({
  name: "productGroups",
  initialState: productGroupAdapter.getInitialState({
    status: {},
    itemStatus: {},
    currentItem: null,
    images: {},
    imageStatus: {},
  }),
  reducers: {
    clearCurrentItem: (state) => {
      state.currentItem = null;
    },
    clearStatus: (state) => {
      state.status = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProductGroupsThunk.pending, (state, action) => {
        state.status = { loading: "get" };
      })
      .addCase(getProductGroupsThunk.rejected, (state, action) => {
        state.status = { error: "get", errorMessage: action.payload?.message };
      })
      .addCase(getProductGroupsThunk.fulfilled, (state, action) => {
        state.status = { success: "get" };
        productGroupAdapter.setAll(state, action.payload);
      });

    builder
      .addCase(getProductGroupImagesThunk.pending, (state, action) => {
        state.imageStatus = { loading: "get" };
      })
      .addCase(getProductGroupImagesThunk.rejected, (state, action) => {
        state.imageStatus = {
          error: "get",
          errorMessage: action.payload?.message,
        };
      })
      .addCase(getProductGroupImagesThunk.fulfilled, (state, action) => {
        state.imageStatus = { success: "get" };
        state.images = { ...state.images, [action.meta.arg]: action.payload };
      });
    builder
      .addCase(setProductGroupImageThunk.pending, (state, action) => {
        state.imageStatus = { loading: "post", id: action.meta.arg.id };
      })
      .addCase(setProductGroupImageThunk.rejected, (state, action) => {
        state.imageStatus = {
          error: "post",
          errorMessage: action.payload?.message,
          id: action.meta.arg.id,
        };
      })
      .addCase(setProductGroupImageThunk.fulfilled, (state, action) => {
        state.imageStatus = { success: "post" };
        productGroupAdapter.updateOne(state, {
          id: action.meta.arg.id,
          changes: action.payload,
        });
      });
    builder
      .addCase(createProductGroupThunk.pending, (state, action) => {
        state.status = { loading: "create" };
      })
      .addCase(createProductGroupThunk.rejected, (state, action) => {
        state.status = {
          error: "create",
          errorMessage: action.payload?.message,
        };
      })
      .addCase(createProductGroupThunk.fulfilled, (state, action) => {
        state.status = { success: "create" };
        productGroupAdapter.addOne(state, { ...action.payload, items: [] });
        state.currentItem = action.payload.id;
      });

    builder
      .addCase(updateProductGroupThunk.pending, (state, action) => {
        state.status = { loading: "update", id: action.meta.arg.id };
      })
      .addCase(updateProductGroupThunk.rejected, (state, action) => {
        state.status = {
          error: "update",
          errorMessage: action.payload?.message,
          id: action.meta.arg.id,
        };
      })
      .addCase(updateProductGroupThunk.fulfilled, (state, action) => {
        state.status = { success: "update" };
        productGroupAdapter.updateOne(state, {
          id: action.meta.arg.id,
          changes: action.payload,
        });
      });

    builder
      .addCase(deleteProductGroupThunk.pending, (state, action) => {
        state.status = { loading: "delete", id: action.meta.arg.id };
      })
      .addCase(deleteProductGroupThunk.rejected, (state, action) => {
        state.status = {
          id: action.meta.arg.id,
          error: "delete",
          errorMessage: action.payload?.message,
        };
      })
      .addCase(deleteProductGroupThunk.fulfilled, (state, action) => {
        state.status = { success: "delete", id: action.meta.arg.id };
        productGroupAdapter.removeOne(state, action.meta.arg);
      });

    // Items

    builder
      .addCase(createProductGroupItemThunk.pending, (state, action) => {
        state.itemStatus = {
          loading: "create",
          group: action.meta.arg.product_group.id,
        };
      })
      .addCase(createProductGroupItemThunk.rejected, (state, action) => {
        state.itemStatus = {
          error: "create",
          group: action.meta.arg.product_group.id,
          errorMessage: action.payload?.message,
        };
      })
      .addCase(createProductGroupItemThunk.fulfilled, (state, action) => {
        state.itemStatus = {
          success: "create",
          group: action.meta.arg.product_group.id,
        };
        productGroupAdapter.updateOne(state, {
          id: action.meta.arg.product_group.id,
          changes: {
            items: [
              ...state.entities[action.meta.arg.product_group.id].items,
              action.payload,
            ],
          },
        });
      });

    builder
      .addCase(deleteProductGroupItemThunk.pending, (state, action) => {
        state.itemStatus = { loading: "delete", id: action.meta.arg.id };
      })
      .addCase(deleteProductGroupItemThunk.rejected, (state, action) => {
        state.itemStatus = {
          error: "delete",
          id: action.meta.arg.id,
          errorMessage: action.payload?.message,
        };
      })
      .addCase(deleteProductGroupItemThunk.fulfilled, (state, action) => {
        state.itemStatus = { success: "delete", id: action.meta.arg.id };
        productGroupAdapter.updateOne(state, {
          id: action.meta.arg.group.id,
          changes: {
            items: action.meta.arg.group.items.filter(
              (item) => item.id !== action.meta.arg.id
            ),
          },
        });
      });
  },
});
export const productGroupSelector = productGroupAdapter.getSelectors(
  (state) => state.productGroup
);
export const { clearCurrentItem, clearStatus } = productGroupsSlice.actions;

export default productGroupsSlice.reducer;
